import { useState } from 'react'
import type { ChildInput, Relationship, SignupPath } from '@lb/domain'
import { Link } from '@tanstack/react-router'
import { Trans, useTranslation } from '@lb/i18n'
import { IconChevronDown, IconClose, cn } from '@lb/ui'

import {
  CheckboxRow,
  Chip,
  Field,
  FormCard,
  FunnelShell,
  PrimaryButton,
  SecondaryButton,
  inputClass,
} from './funnel-shell'

/**
 * The funnel's middle steps (spec §3.2–§3.3).
 *
 * Validation is on blur, inline, under the field. The server's rules are
 * mirrored here so a member is told before the round trip — but the server
 * remains the authority, and its `VALIDATION_FAILED` maps straight onto the
 * same fields because the backend returns a Zod flatten.
 */

const RELATIONSHIPS: { value: Relationship; }[] = [
  { value: 'mom' },
  { value: 'dad' },
  { value: 'grandmother' },
  { value: 'grandfather' },
  { value: 'prefer_not_to_say' },
]

export interface AccountValues {
  fullName: string
  email: string
  password: string
  relationship?: Relationship
  /** The newsletter box — sent as `SignupAccount.marketingConsent`. */
  marketingConsent?: boolean
}

export function AccountStep({
  path,
  initial,
  serverErrors,
  busy,
  onBack,
  onSubmit,
}: {
  path: SignupPath
  initial: Partial<AccountValues>
  serverErrors?: Record<string, string[]>
  busy?: boolean
  onBack: () => void
  onSubmit: (values: AccountValues) => void
}) {
  const { t } = useTranslation()
  const [values, setValues] = useState<AccountValues>({
    fullName: initial.fullName ?? '',
    email: initial.email ?? '',
    password: '',
    relationship: initial.relationship,
  })
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [showPassword, setShowPassword] = useState(false)
  /*
   * The design's required acceptance. It gates Continue and is NOT transmitted:
   * `SignupAccount` has no field for it, so the record of consent is the signup
   * itself. Flagged for the backend rather than faked into another field.
   */
  const [consent, setConsent] = useState(false)
  /*
   * The newsletter opt-in from the approved onboarding HTML (ob-screens.jsx
   * L178-181). Transmitted as `marketingConsent` since 21 Sept 2026: it is what
   * puts the member on the Kit list, and only a ticked box does.
   */
  const [newsletter, setNewsletter] = useState(initial.marketingConsent ?? false)

  const errors: Record<string, string | undefined> = {
    fullName: values.fullName.trim() ? undefined : t('onboarding.fullNameRequired'),
    email: /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email)
      ? undefined
      : t('onboarding.emailInvalid'),
    // The server's rule, mirrored. `min(12)` is the contract's, not ours.
    password: values.password.length >= 12 ? undefined : t('onboarding.passwordTooShort'),
  }

  const show = (key: string) => (touched[key] ? errors[key] : undefined) ?? serverErrors?.[key]?.[0]
  const valid = !errors.fullName && !errors.email && !errors.password && consent

  return (
    <FunnelShell
      step={1}
      path={path}
      back={{ label: t('onboarding.back'), onClick: onBack }}
      title={t('onboarding.accountTitle')}
      lede={t('onboarding.accountLede')}
    >
      <FormCard>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          setTouched({ fullName: true, email: true, password: true })
          if (valid) onSubmit({ ...values, marketingConsent: newsletter })
        }}
      >
        {/*
          * One name field, not two. The backend's reasoning and it is right:
          * "names do not reliably split into first and last."
          *
          * Field order follows the approved onboarding HTML (ob-screens.jsx
          * L122-176): full name → I am (parent only) → email → password. The
          * relationship chips move BEFORE the email so a hesitation on the
          * first question that most parents can answer instantly is not the
          * one that stops them.
          */}
        <Field label={t('onboarding.fullName')} error={show('fullName')}>
          <input
            className={inputClass}
            placeholder={t('onboarding.fullNamePlaceholder')}
            value={values.fullName}
            autoComplete="name"
            onBlur={() => setTouched((t) => ({ ...t, fullName: true }))}
            onChange={(e) => setValues((v) => ({ ...v, fullName: e.target.value }))}
          />
        </Field>

        {path === 'PARENT' ? (
          <fieldset className="mb-5">
            <legend className="mb-[7px] text-[13px] font-semibold text-ink-soft">
              {t('onboarding.iAm')}
            </legend>
            {/* role=radiogroup on chip rows is an explicit handoff requirement. */}
            <div role="radiogroup" aria-label={t('onboarding.iAmLabel')} className="flex flex-wrap gap-2">
              {RELATIONSHIPS.map((option) => (
                <Chip
                  key={option.value}
                  role="radio"
                  aria-checked={values.relationship === option.value}
                  selected={values.relationship === option.value}
                  onClick={() => setValues((v) => ({ ...v, relationship: option.value }))}
                >
                  {t(`onboarding.relationship.${option.value}`)}
                </Chip>
              ))}
            </div>
          </fieldset>
        ) : null}

        <Field label={t('onboarding.email')} error={show('email')} hint={t('onboarding.emailLoginHint')}>
          <input
            className={inputClass}
            type="email"
            placeholder={t('onboarding.emailPlaceholder')}
            value={values.email}
            autoComplete="email"
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
          />
        </Field>

        {/* `.ob-pw-wrap` — the reveal sits inside the field. A password rule of
         * twelve characters without a way to check what was typed is how a typo
         * becomes three failed attempts. */}
        <Field
          label={t('onboarding.password')}
          error={show('password')}
          hint={t('onboarding.passwordHint')}
        >
          <span className="relative block">
            <input
              className={cn(inputClass, 'pe-[74px]')}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('onboarding.passwordPlaceholder')}
              value={values.password}
              autoComplete="new-password"
              onBlur={() => setTouched((t) => ({ ...t, password: true }))}
              onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              aria-pressed={showPassword}
              className="absolute end-1.5 top-1/2 -translate-y-1/2 rounded-lg px-2.5 py-1.5 text-[12.5px] font-semibold text-slate hover:bg-cream-dark"
            >
              {showPassword ? t('onboarding.hide') : t('onboarding.show')}
            </button>
          </span>
        </Field>

        <CheckboxRow checked={newsletter} onChange={setNewsletter}>
          {t('onboarding.newsletterOptIn')}
        </CheckboxRow>

        <CheckboxRow checked={consent} onChange={setConsent}>
          <Trans
            i18nKey="onboarding.acceptTerms"
            components={{
              terms: <Link to="/legal/terms" className="underline" />,
              privacy: <Link to="/legal/privacy" className="underline" />,
            }}
          />{' '}
          <span className="text-muted">({t('onboarding.required')})</span>
        </CheckboxRow>

        {/* `.ob-form-actions` — one full-width primary, stacked. Back is at the
         * top of the column now, so it is not a neighbour of Continue. */}
        <div className="mt-7">
          <PrimaryButton type="submit" busy={busy} disabled={!valid}>
            {t('onboarding.continue')}
          </PrimaryButton>
        </div>
      </form>
      </FormCard>
    </FunnelShell>
  )
}

/**
 * The crew step — optional, and genuinely so (spec §3.3).
 *
 * Follows the approved onboarding HTML (`ob-screens.jsx` L236-278). Initial
 * state is genuinely empty — the "+ Add a child" button is the whole card
 * until the parent chooses to add one — matching the mock's "everything here
 * is optional" framing rather than pre-inserting an empty row that reads as a
 * required question.
 *
 * "Skip for now" is equally weighted, not a de-emphasised link. The design
 * says so and it matters: a parent who does not want to type their children's
 * names into a form should not have to hunt for the way past.
 *
 * 🔴 Worth knowing: there is no crew route after signup, so skipping is
 * currently permanent. Requested as round-3 §6.
 *
 * The co-parent's first name is persisted now: `StartCheckout.coParentName`
 * is what the funnel sends and `GET /api/me/crew` is what the sidebar reads
 * it back from. It used to be collected here and dropped at submit time.
 */
export function CrewStep({
  initial,
  initialCoParent,
  busy,
  onBack,
  onSubmit,
}: {
  initial: ChildInput[]
  initialCoParent?: string
  busy?: boolean
  onBack: () => void
  onSubmit: (payload: { children: ChildInput[]; coParent?: string }) => void
}) {
  const { t } = useTranslation()
  const [children, setChildren] = useState<ChildInput[]>(initial)
  const [coParent, setCoParent] = useState(initialCoParent ?? '')
  const thisYear = new Date().getFullYear()

  const named = children.filter((child) => child.name.trim().length > 0)

  return (
    <FunnelShell
      step={2}
      path="PARENT"
      back={{ label: t('onboarding.back'), onClick: onBack }}
      title={t('onboarding.crewTitle')}
      lede={t('onboarding.crewLede')}
    >
      <FormCard>
      <div className="flex flex-col gap-6">
        {/*
         * "Your children" is one field, whether it holds zero rows or twelve.
         * The label + add button + helper text form the field; individual kid
         * rows are the content inside it.
         */}
        <div>
          <div className="mb-2 text-[13px] font-semibold text-ink-soft">
            {t('onboarding.yourChildren')}{' '}
            <span className="font-normal text-muted">({t('onboarding.optional')})</span>
          </div>

          {children.length > 0 ? (
            <div className="mb-3 flex flex-col gap-3">
              {children.map((child, index) => (
                <div key={index} className="flex flex-col gap-3 sm:flex-row sm:items-start">
                  <div className="flex-1">
                    <Field label={t('onboarding.firstName')}>
                      <input
                        className={inputClass}
                        value={child.name}
                        placeholder={t('onboarding.childPlaceholder', { n: index + 1 })}
                        onChange={(e) =>
                          setChildren((list) =>
                            list.map((c, i) => (i === index ? { ...c, name: e.target.value } : c)),
                          )
                        }
                      />
                    </Field>
                  </div>
                  {/*
                   * The approved mock offers an "Age" dropdown (1..18), not a
                   * year input. The backend contract still stores `birthYear`
                   * (a year is stable, an age is wrong within twelve months of
                   * being typed) — so the UI asks for an age and we convert on
                   * change. Client-side ergonomics + server-side truth.
                   */}
                  <div className="sm:w-32">
                    <Field label={t('onboarding.age')}>
                      {/*
                       * Native <select> chevrons render differently across
                       * OS/browsers and sit awkwardly against a rounded pill
                       * — reported inconsistent between the two dropdowns in
                       * the same crew row. Strip the native marker with
                       * `appearance-none`, reserve right-side padding, and
                       * draw one chevron ourselves so both rows read
                       * identically.
                       */}
                      <div className="relative">
                        <select
                          className={cn(inputClass, 'appearance-none pe-10')}
                          value={child.birthYear ? String(thisYear - child.birthYear) : ''}
                          onChange={(e) => {
                            const age = Number.parseInt(e.target.value, 10)
                            setChildren((list) =>
                              list.map((c, i) =>
                                i === index
                                  ? { ...c, birthYear: Number.isFinite(age) ? thisYear - age : undefined }
                                  : c,
                              ),
                            )
                          }}
                        >
                          <option value="">{t('onboarding.age')}</option>
                          {/* 0 first — a newborn is a crew member too, and the
                              QA round found no way to add one. `<1` maps to a
                              birth year of this year. */}
                          {Array.from({ length: 19 }, (_, i) => (
                            <option key={i} value={String(i)}>
                              {i === 0 ? '<1' : i}
                            </option>
                          ))}
                        </select>
                        <IconChevronDown
                          size={16}
                          className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2 text-muted"
                        />
                      </div>
                    </Field>
                  </div>
                  <button
                    type="button"
                    onClick={() => setChildren((list) => list.filter((_, i) => i !== index))}
                    aria-label={t('onboarding.removeChild', { n: index + 1 })}
                    className="mt-1 min-h-[44px] self-start rounded-lg px-3 text-[13px] font-semibold text-slate hover:bg-cream-dark hover:text-ink sm:mt-7"
                  >
                    <IconClose size={14} title={null} />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {/* `.ob-kid-add` — a dashed add row, not a bare text link. */}
          {children.length < 12 ? (
            <button
              type="button"
              onClick={() => setChildren((list) => [...list, { name: '' }])}
              className="min-h-[48px] w-full rounded-xl border-[1.5px] border-dashed border-border-deep text-[13.5px] font-semibold text-slate transition-colors hover:border-ink-soft hover:text-ink"
            >
              {children.length === 0 ? t('onboarding.addChild') : t('onboarding.addAnotherChild')}
            </button>
          ) : null}

          <p className="mt-2 text-[12.5px] text-muted">{t('onboarding.childrenHint')}</p>
        </div>

        {/*
         * Co-parent name — client-side only until the backend contract adds
         * the field. Same pattern as `newsletter` on the account step.
         */}
        <div>
          <div className="mb-2 text-[13px] font-semibold text-ink-soft">
            {t('onboarding.coParentName')}{' '}
            <span className="font-normal text-muted">({t('onboarding.optional')})</span>
          </div>
          <input
            className={inputClass}
            value={coParent}
            placeholder={t('onboarding.coParentPlaceholder')}
            onChange={(e) => setCoParent(e.target.value)}
          />
        </div>

        {/*
         * Two equally-weighted actions, stacked and full width.
         *
         * The design is explicit that "Skip for now" is not a de-emphasised
         * link — a parent who does not want to type their children's names
         * must not have to hunt for the way past.
         */}
        <div className="mt-2 flex flex-col gap-3">
          <PrimaryButton
            busy={busy}
            onClick={() =>
              onSubmit({
                children: named,
                ...(coParent.trim() ? { coParent: coParent.trim() } : {}),
              })
            }
          >
            {t('onboarding.continue')}
          </PrimaryButton>
          <SecondaryButton onClick={() => onSubmit({ children: [] })}>
            {t('onboarding.skip')}
          </SecondaryButton>
        </div>
      </div>
      </FormCard>
    </FunnelShell>
  )
}

/**
 * The five `milieux` chips from the approved onboarding HTML
 * (`ob-paths.jsx` L217). Multi-select — a practitioner may work across CPE +
 * private practice + a school, and the chip group's whole point is not
 * forcing them to pick one.
 */
const WORK_SETTINGS = ['cpe', 'primary', 'secondary', 'private', 'other'] as const
type WorkSetting = (typeof WORK_SETTINGS)[number]

export function PractitionerStep({
  path,
  busy,
  onBack,
  onSubmit,
}: {
  path: SignupPath
  busy?: boolean
  onBack: () => void
  onSubmit: (values: { profession: string; setting?: string; licenceBody?: string; experienceYears?: number }) => void
}) {
  const { t } = useTranslation()
  const [profession, setProfession] = useState('')
  const [milieux, setMilieux] = useState<WorkSetting[]>([])
  /*
   * Years of experience — 0..40, default 8 per the approved slider. Client-
   * side only for now; `PractitionerInput` has no field for it. Flagged along
   * with the newsletter opt-in and co-parent name.
   */
  const [expYears, setExpYears] = useState(8)
  const [touched, setTouched] = useState(false)

  const professionError = profession.trim() ? undefined : t('onboarding.professionRequired')
  const milieuError = milieux.length > 0 ? undefined : t('onboarding.workSettingRequired')
  const canGo = !professionError && !milieuError

  const toggleMilieu = (m: WorkSetting) =>
    setMilieux((current) =>
      current.includes(m) ? current.filter((x) => x !== m) : [...current, m],
    )

  return (
    <FunnelShell
      step={2}
      path={path}
      back={{ label: t('onboarding.back'), onClick: onBack }}
      title={t('onboarding.proTitle')}
      lede={t('onboarding.proLede')}
    >
      <FormCard>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          setTouched(true)
          if (canGo) {
            // The wire field is a single string; the approved chip set joins
            // with commas. Server sees "École primaire, CPE / garderie".
            const settingCsv = milieux.map((m) => t(`onboarding.workSetting.${m}`)).join(', ')
            onSubmit({
              profession,
              setting: settingCsv || undefined,
              // The slider's value finally goes somewhere — it was collected
              // and dropped, which is the inert-field rule broken on the
              // first screen a practitioner ever fills in.
              experienceYears: expYears,
            })
          }
        }}
      >
        <Field
          label={t('onboarding.profession')}
          error={touched ? professionError : undefined}
        >
          <input
            className={inputClass}
            placeholder={t('onboarding.professionPlaceholder')}
            value={profession}
            onBlur={() => setTouched(true)}
            onChange={(e) => setProfession(e.target.value)}
          />
        </Field>

        <fieldset className="mb-5">
          <legend className="mb-[7px] text-[13px] font-semibold text-ink-soft">
            {t('onboarding.workSettings')}{' '}
            <span className="font-normal text-muted">
              ({t('onboarding.workSettingsHint')})
            </span>
          </legend>
          <div
            role="group"
            aria-label={t('onboarding.workSettings')}
            className="flex flex-wrap gap-2"
          >
            {WORK_SETTINGS.map((m) => (
              <Chip
                key={m}
                aria-pressed={milieux.includes(m)}
                selected={milieux.includes(m)}
                onClick={() => toggleMilieu(m)}
              >
                {t(`onboarding.workSetting.${m}`)}
              </Chip>
            ))}
          </div>
          {touched && milieuError ? (
            <p className="mt-2 text-[12.5px] text-ink">{milieuError}</p>
          ) : null}
        </fieldset>

        <div className="mb-5">
          <div className="mb-2 flex items-baseline justify-between">
            <label htmlFor="pro-exp" className="text-[13px] font-semibold text-ink-soft">
              {t('onboarding.expYears')}{' '}
              <span className="font-normal text-muted">
                ({t('onboarding.optional')})
              </span>
            </label>
            <span className="text-[13px] font-semibold text-ink" aria-live="polite">
              {expYears}
            </span>
          </div>
          <input
            id="pro-exp"
            type="range"
            min={0}
            max={40}
            step={1}
            value={expYears}
            onChange={(e) => setExpYears(Number.parseInt(e.target.value, 10))}
            className="w-full accent-[var(--ink)]"
          />
        </div>

        <div className="mt-7">
          <PrimaryButton type="submit" busy={busy} disabled={!canGo}>
            {t('onboarding.continue')}
          </PrimaryButton>
        </div>
      </form>
      </FormCard>
    </FunnelShell>
  )
}

export function OrganisationStep({
  busy,
  seats,
  contactName,
  onBack,
  onSubmit,
}: {
  busy?: boolean
  /** Chosen on the Plan & seats screen — shown, not re-asked. */
  seats: number
  /** The account holder's name, prefilled — they are the organisation admin. */
  contactName: string
  onBack: () => void
  onSubmit: (values: {
    name: string
    type: 'SCHOOL' | 'CPE' | 'CLINIC' | 'CIUSSS' | 'OTHER'
    billingEmail: string
    billingContactName: string
    neq?: string
    billingAddress: string
  }) => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [type, setType] = useState<'SCHOOL' | 'CPE' | 'CLINIC' | 'CIUSSS' | 'OTHER'>('CPE')
  const [contact, setContact] = useState(contactName)
  const [billingEmail, setBillingEmail] = useState('')
  const [neq, setNeq] = useState('')
  const [billingAddress, setBillingAddress] = useState('')
  const [touched, setTouched] = useState(false)

  /*
   * Through `t`, not literals. These four were hardcoded French, so an English
   * signup was refused in a language it had not been filled in — the same
   * defect the per-path step-label registry was fixed for.
   */
  const errors = {
    name: name.trim() ? undefined : t('onboarding.orgNameRequired'),
    contact: contact.trim() ? undefined : t('onboarding.orgContactRequired'),
    billingEmail: /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(billingEmail)
      ? undefined
      : t('onboarding.emailInvalid'),
    billingAddress: billingAddress.trim() ? undefined : t('onboarding.orgAddressRequired'),
  }
  const valid = !errors.name && !errors.contact && !errors.billingEmail && !errors.billingAddress

  const KINDS = ['SCHOOL', 'CPE', 'CLINIC', 'CIUSSS', 'OTHER'] as const

  return (
    <FunnelShell
      step={2}
      path="ORG"
      back={{ label: t('onboarding.back'), onClick: onBack }}
      title={t('onboarding.orgTitle')}
      lede={t('onboarding.orgLede')}
    >
      <FormCard>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          setTouched(true)
          if (valid)
            onSubmit({
              name: name.trim(),
              type,
              billingEmail: billingEmail.trim(),
              billingContactName: contact.trim(),
              ...(neq.trim() ? { neq: neq.trim() } : {}),
              billingAddress: billingAddress.trim(),
            })
        }}
      >
        <Field label={t('onboarding.orgName')} error={touched ? errors.name : undefined}>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('onboarding.orgNamePlaceholder')}
          />
        </Field>

        {/* The form's five kinds — a closed set drawn as chips, stored as the enum. */}
        <div className="mb-5">
          <p className="mb-2 text-[14px] font-semibold text-ink">{t('onboarding.orgType')}</p>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('onboarding.orgType')}>
            {KINDS.map((kind) => (
              <button
                key={kind}
                type="button"
                role="radio"
                aria-checked={type === kind}
                onClick={() => setType(kind)}
                className={
                  type === kind
                    ? 'min-h-[44px] rounded-full border border-transparent bg-selected px-5 text-[14px] font-semibold text-ink-on-tint'
                    : 'min-h-[44px] rounded-full border border-border px-5 text-[14px] text-ink-soft hover:border-ink-soft'
                }
              >
                {t(`onboarding.orgKind.${kind}`)}
              </button>
            ))}
          </div>
        </div>

        <Field label={t('onboarding.orgContact')} error={touched ? errors.contact : undefined}>
          <input className={inputClass} value={contact} onChange={(e) => setContact(e.target.value)} />
        </Field>

        <Field label={t('onboarding.orgProEmail')} error={touched ? errors.billingEmail : undefined}>
          <input
            className={inputClass}
            type="email"
            value={billingEmail}
            onChange={(e) => setBillingEmail(e.target.value)}
            placeholder={t('onboarding.orgEmailPlaceholder')}
          />
        </Field>

        <Field
          label={`${t('onboarding.orgNeq')} ${t('onboarding.optional')}`}
        >
          <input
            className={inputClass}
            value={neq}
            onChange={(e) => setNeq(e.target.value)}
            placeholder={t('onboarding.orgNeqHint')}
          />
        </Field>

        <Field label={t('onboarding.orgAddress')} error={touched ? errors.billingAddress : undefined}>
          <input
            className={inputClass}
            value={billingAddress}
            onChange={(e) => setBillingAddress(e.target.value)}
            placeholder={t('onboarding.orgAddressHint')}
          />
        </Field>

        {/* Seats travel from the Plan & seats screen — restated, not re-asked. */}
        <p className="mb-5 text-[13px] text-muted">
          {t('onboarding.seats', { count: seats })} · {t('onboarding.orgSeatsMore')}
        </p>

        <div className="mt-2">
          <PrimaryButton type="submit" busy={busy}>
            {t('onboarding.continue')}
          </PrimaryButton>
        </div>
      </form>
      </FormCard>
    </FunnelShell>
  )
}

